import { useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { cn } from '@/lib/utils';

/**
 * Lightbox del People Landing: click en una foto (marquee, cards solo-imagen,
 * bloque imagen) → pantalla completa con anterior/siguiente, flechas del
 * teclado, Esc y contador. Portal al body para escapar del overflow de la
 * grilla. El estado por bloque lo da `useLightbox()` (useLightbox.tsx).
 */
export function LandingLightbox({
  images,
  index,
  onIndex,
  onClose,
}: {
  images: string[];
  index: number;
  onIndex: (i: number) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const reduce = useReducedMotion();
  const many = images.length > 1;
  const prev = useCallback(() => onIndex((index - 1 + images.length) % images.length), [index, images.length, onIndex]);
  const next = useCallback(() => onIndex((index + 1) % images.length), [index, images.length, onIndex]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowLeft' && many) prev();
      else if (e.key === 'ArrowRight' && many) next();
    };
    window.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose, prev, next, many]);

  const btn =
    'absolute z-10 flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-white backdrop-blur-sm border border-white/20 hover:bg-white/20 transition-colors';

  return createPortal(
    <AnimatePresence>
      <motion.div
        role="dialog"
        aria-modal="true"
        aria-label={t('people_landing.lightbox.title', 'Foto ampliada')}
        className="fixed inset-0 z-100 flex items-center justify-center bg-black/90 backdrop-blur-xs"
        initial={reduce ? false : { opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <button type="button" className={cn(btn, 'right-4 top-4')} onClick={onClose} aria-label={t('common.ui.close', 'Cerrar')}>
          <X className="h-5 w-5" />
        </button>
        {many && (
          <>
            <button
              type="button"
              className={cn(btn, 'left-3 md:left-6 top-1/2 -translate-y-1/2')}
              onClick={(e) => { e.stopPropagation(); prev(); }}
              aria-label={t('people_landing.lightbox.prev', 'Anterior')}
            >
              <ChevronLeft className="h-6 w-6" />
            </button>
            <button
              type="button"
              className={cn(btn, 'right-3 md:right-6 top-1/2 -translate-y-1/2')}
              onClick={(e) => { e.stopPropagation(); next(); }}
              aria-label={t('people_landing.lightbox.next', 'Siguiente')}
            >
              <ChevronRight className="h-6 w-6" />
            </button>
          </>
        )}
        <motion.img
          key={images[index]}
          src={images[index]}
          alt=""
          className="max-h-[88vh] max-w-[92vw] rounded-2xl object-contain shadow-2xl select-none"
          initial={reduce ? false : { opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.25, ease: 'easeOut' }}
          onClick={(e) => { e.stopPropagation(); if (many) next(); }}
          draggable={false}
        />
        {many && (
          <div className="absolute bottom-5 left-1/2 -translate-x-1/2 rounded-full bg-white/10 px-3 py-1 text-xs text-white/80 backdrop-blur-sm">
            {index + 1} / {images.length}
          </div>
        )}
      </motion.div>
    </AnimatePresence>,
    document.body
  );
}
